import {
  IRobotPreview,
  IRobotPreviewConfiguration,
  IRobotPreviewPage,
  IRobotPreviewSentence,
  IRobotPreviewSentenceConfiguration,
  IRobotPreviewWordClick,
  IRobotPreviewWordClickConfiguration,
  IRobotSentence,
  IRobotWordClick,
  IRobotWordSuggestion,
  IRobotWordTableConfiguration,
} from './interfaces/robots.interface';
import { IPageDimensions } from './interfaces/page-dimensions.interface';
import {
  ClassificationParameterTypeEnum,
  IClassificationParameters,
  IClassificationParametersPage,
} from './interfaces/classification-parameters.interface';
import { SentenceTypeEnum } from './enums/sentence-type.enum';
import { ILandmark } from './interfaces/landmarks.interface';
import { ISmartCanvasClickedItemEvent } from './interfaces/smart-canvas.interface';
import { GeneratorsUtility } from '../utilities/generators';
import { environment } from 'src/environments/environment';
import { IGroupConfigurationKey } from './interfaces/group-keys.interface';

export class ClassificationParameters implements IClassificationParameters {
  private _stateJson: { [key: string]: any } = {};
  public name: string;
  public robotId: string;
  public type: ClassificationParameterTypeEnum = ClassificationParameterTypeEnum.ocr;
  public id: string;
  public classificationId: string;
  public sentences: RobotSentence[] = [];
  public createDate!: Date;
  public createUser!: string;
  public updateDate!: Date;
  public updateUser!: string;
  public deleteDate?: Date;
  public discriminator?: string;
  public fileIds: string[] = [];
  public wordClicks: RobotWordClick[] = [];
  public key: string;
  public version = '2.0';
  public pages?: ClassificationParametersPage[] = [];

  public get state(): string {
    return this._stateJson ? JSON.stringify(this._stateJson) : null;
  }

  /**
   * @deprecated usando enquanto não estivermos salvando o tipo de documento (groupId) no backend.
   * Depois o state não poderá ser alterado pelo frontend em nenhum momento.
   */
  public set state(state: string | { [key: string]: any }) {
    this._stateJson = typeof state === 'string' ? JSON.parse(state) : state;
  }

  public get isNiboDocsRobot(): boolean {
    return this.robotId === environment.niboDocsRobotId;
  }

  constructor(classificationParameterInterface?: IClassificationParameters) {
    if (classificationParameterInterface) {
      Object.assign(this, classificationParameterInterface);
    }

    /// TODO: criar essas classes no serviço. Precisamos primeiro fazer com que o cache saiba fazer isso.
    if (this.sentences) {
      this.sentences = this.sentences.map((sentence) => new RobotSentence(sentence));
    }

    if (this.wordClicks) {
      this.wordClicks = this.wordClicks.map((wordClick) => new RobotWordClick(wordClick));
    }
  }

  public getStateItem<T>(key: string): T {
    return this._stateJson[key];
  }

  public addStateItem<T>(key: string, value: T): this {
    this._stateJson[key] = value;
    return this;
  }

  /**
   * Necessário para, ao passar a classe para o backend, não perder os campos com GETS e SETS.
   * https://stackoverflow.com/questions/46495497/typescript-getter-field-not-going-through-http-param?noredirect=1&lq=1
   */
  public toJson(): { [key: string]: any } {
    return {
      ...this,
      pages: this.pages ? this.pages.map((page) => page.toJson()) : undefined,
      state: this._stateJson ? JSON.stringify(this._stateJson) : null,
    };
  }
}

export class ClassificationParametersPage implements IClassificationParametersPage {
  private _stateJson: { [key: string]: any } = {};
  public _groupId?: string;
  public wordClicks: RobotWordClick[] = [];
  public isComplementary = false;
  public sentences: RobotSentence[] = [];

  public set state(state: string | { [key: string]: any }) {
    if (state === '') {
      this._stateJson = {};
    } else {
      this._stateJson = typeof state === 'string' ? JSON.parse(state) : state || {};
    }
  }

  public get state(): string {
    return this._stateJson ? JSON.stringify(this._stateJson) : null;
  }

  constructor(page?: IClassificationParametersPage) {
    if (page) {
      Object.assign(this, page);
    }
  }

  public getStateItem<T>(key: string): T {
    return this._stateJson[key];
  }

  public addStateItem<T>(key: string, value: T): this {
    this._stateJson[key] = value;
    return this;
  }

  /**
   * Necessário para, ao passar a classe para o backend, não perder os campos com GETS e SETS.
   * https://stackoverflow.com/questions/46495497/typescript-getter-field-not-going-through-http-param?noredirect=1&lq=1
   */
  public toJson(): { [key: string]: any } {
    return {
      ...this,
      state: this._stateJson ? JSON.stringify(this._stateJson) : null,
    };
  }
}

export class RobotWordClick implements IRobotWordClick {
  public readonly _id: number;
  public _pageNumber?: number;
  public word: IRobotWordSuggestion;
  public extractionThreshold = 0.03;
  public landmarks?: ILandmark[];
  public tableConfiguration?: IRobotWordTableConfiguration;
  public xPath?: string;
  public polygon?: Polygon;

  constructor(wordClick: IRobotWordClick) {
    Object.assign(this, wordClick);
    this._id = GeneratorsUtility.generateHashFromString(this.word.key);
    this.word = wordClick.word;
  }
}

export class RobotSentence implements IRobotSentence {
  public readonly _id: number;
  public text: string;
  public polygon: Polygon;
  public identificationThreshold: number;
  public pattern?: string = null;
  public type: SentenceTypeEnum;
  public xPath?: string;
  public isDenied = false;

  constructor(robotSentence: IRobotSentence) {
    Object.assign(this, robotSentence);
    this.identificationThreshold = robotSentence.identificationThreshold || 0.03;
    if (robotSentence.type !== SentenceTypeEnum.XPath && this.polygon.boundingBox) {
      this._id = GeneratorsUtility.generateHashFromBoundingBox(this.polygon.boundingBox);
    } else {
      if (robotSentence.xPath) {
        this._id = GeneratorsUtility.generateHashFromString(robotSentence.xPath);
      } else if (robotSentence.text) {
        this._id = GeneratorsUtility.generateHashFromString(robotSentence.text);
      } else {
        this._id = 0;
      }
    }
  }
}

export class RobotXPATHSentence extends RobotSentence {
  public readonly _id: number;
  public readonly type: SentenceTypeEnum = SentenceTypeEnum.XPath;
  public xPath?: string;
  public readonly text = '';

  constructor(xPath: string) {
    super({
      type: SentenceTypeEnum.XPath,
      xPath: '',
      isDenied: false,
    });
    this.xPath = xPath;
    this._id = GeneratorsUtility.generateHashFromString(this.xPath);
  }
}

export class RobotPreview implements IRobotPreview {
  public fileId: string;
  public pages: RobotPreviewPage[];

  constructor(preview: IRobotPreview) {
    Object.assign(this, preview);

    this.pages = preview.pages.map((page) => {
      return new RobotPreviewPage(page);
    });
  }

  public get allSentencesValid(): boolean {
    return this.pages.filter((page) => page.allSentencesTestsPassed).length > 0;
  }

  public get allWordClicksValid(): boolean {
    return this.pagesWithNotFoundWords.length === 0;
  }

  public get pagesWithNotFoundSentences(): RobotPreviewPage[] {
    return this.pages.filter((page) => !page.allSentencesTestsPassed);
  }

  public get pagesNumberWithNotFoundSentences(): number[] {
    return this.pages.filter((page) => !page.allSentencesTestsPassed).map((page) => page.page);
  }

  public get pagesWithNotFoundWords(): RobotPreviewPage[] {
    return this.pages.filter((page) => page.notFoundWords.length > 0);
  }

  public get pagesNumberWithNotFoundWords(): number[] {
    return this.pagesWithNotFoundWords.map((page) => page.page);
  }
}

export class RobotPreviewPage implements IRobotPreviewPage {
  public readonly sentences: IRobotPreviewSentence[];
  public readonly wordClicks: IRobotPreviewWordClick[];
  public readonly page: number;
  public readonly allSentencesTestsPassed: boolean;
  public readonly allWordClicksTestsPassed: boolean;
  public readonly isWordClickWarning: boolean;

  constructor(page: IRobotPreviewPage) {
    Object.assign(this, page);
  }

  public get notFoundWords(): string[] {
    return this.wordClicks.filter((wordClick) => !wordClick.isPassed).map((wordClick) => wordClick.key);
  }
}

/**
 * Construi um objeto de configuração de extração de dados a partir de um item selecionado no Canvas.
 * @param click Item selecionado no Canvas.
 */
export class RobotWordClickFromSmartCanvasClick extends RobotWordClick {
  constructor(click: ISmartCanvasClickedItemEvent) {
    const contents = click.selectedArea.polygonsInside.flatMap((x) => x.content);
    const hasProcessedValues = contents.flatMap((x) => x.processedValues).length > 0;

    super({
      word: {
        originalText: contents.flatMap((x) => x.text).join(' '),
        word: hasProcessedValues ? contents.flatMap((x) => x.processedValues)[0].value : contents.flatMap((x) => x.text).join(' '), /// TODO: Rever. Não pode pegar o primeiro pois pode ter mais de um.
        dataType: click.groupKey?.type,
        key: click.groupKey?.key,
      },
      landmarks: [],
      _pageNumber: click.selectedArea.pageNumber,
      polygon: new Polygon(click.selectedArea.boundingBox),
    });
  }
}

export class RobotWordClickXPath extends RobotWordClick {
  constructor(xPath: string, groupKey: IGroupConfigurationKey) {
    super({
      word: {
        key: groupKey.key,
        dataType: groupKey.type,
        word: null,
      },
      xPath: xPath,
    });
  }
}

export class RobotPreviewConfiguration implements IRobotPreviewConfiguration {
  public fileId: string;
  public sentencesConfiguration: RobotPreviewSentenceConfiguration[];
  public wordclicksConfiguration: RobotPreviewWordClickConfiguration[];
  public disableWordClicksConfiguration: boolean;

  constructor(fileId: string, sentences: IRobotSentence[], keys: IGroupConfigurationKey[], disableWordClicksConfiguration = true) {
    this.fileId = fileId;
    this.disableWordClicksConfiguration = disableWordClicksConfiguration;

    this.sentencesConfiguration = sentences.map((sentence) => {
      return new RobotPreviewSentenceConfiguration(sentence);
    });

    this.wordclicksConfiguration = keys.map((key) => {
      return {
        isRequired: key.required || false,
        key: key.key,
        valueExpected: null,
      };
    });
  }
}

export class RobotPreviewSentenceConfiguration implements IRobotPreviewSentenceConfiguration {
  public sentence: IRobotSentence;
  public isExpectedToBeFound: boolean;

  constructor(sentence: IRobotSentence, isExpectedToBeFound = true) {
    this.sentence = sentence;
    this.isExpectedToBeFound = sentence.isDenied ? !isExpectedToBeFound : isExpectedToBeFound;
  }
}

export class RobotPreviewWordClickConfiguration implements IRobotPreviewWordClickConfiguration {
  public readonly key: string;
  public valueExpected: any;
  public isRequired?: boolean;

  constructor(key: string, valueExpected: any, isRequired = false) {
    this.key = key;
    this.valueExpected = valueExpected;
    this.isRequired = isRequired;
  }
}

export class Polygon {
  public boundingBox: number[];
  public centroid!: Point2D;
  public coordinates: Point2D[];

  constructor(boundingBox: number[]) {
    this.coordinates = this._processCoordinates(boundingBox);
    this._calculateCentroid();
    this.boundingBox = boundingBox;
  }

  private _processCoordinates(boundingBox: number[]): Point2D[] {
    const result: Point2D[] = [];
    for (let index = 0; index < boundingBox.length; index += 2) {
      result.push(new Point2D(boundingBox[index], boundingBox[index + 1]));
    }

    return result;
  }

  private _calculateCentroid(): void {
    const xValues = this.coordinates.map((pt) => pt.x);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);

    const yValues = this.coordinates.map((pt) => pt.y);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    this.centroid = new Point2D((maxX + minX) / 2, (maxY + minY) / 2);
  }

  public createBoundingBox(pageDimension: IPageDimensions): void {
    this.boundingBox = [];

    for (let i = 0; i < this.coordinates.length; i++) {
      this.boundingBox.push(this.coordinates[i].x * pageDimension.width);
      this.boundingBox.push(this.coordinates[i].y * pageDimension.height);
    }
  }
}

export class Point2D {
  public x: number;
  public y: number;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }
}
